import { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ImageIcon } from 'lucide-react';

const resolvePhoto = (photo) => {
    if (!photo) return null;
    return photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${photo}` : photo;
};

/**
 * Simple, reliable photo carousel for property cards.
 * Uses state + CSS transform. No external lib needed.
 * Falls back to a single image or placeholder if 0/1 photo.
 */
export const PhotoCarousel = ({ photos = [], alt = '', placeholder, badge, className = 'h-44', showCounter = true }) => {
    const safePhotos = Array.isArray(photos) ? photos.filter(Boolean) : [];
    const [index, setIndex] = useState(0);

    // Reset to 0 when photos change
    useEffect(() => { setIndex(0); }, [safePhotos.length]);

    const goPrev = useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setIndex((i) => (i === 0 ? safePhotos.length - 1 : i - 1));
    }, [safePhotos.length]);

    const goNext = useCallback((e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setIndex((i) => (i === safePhotos.length - 1 ? 0 : i + 1));
    }, [safePhotos.length]);

    const goTo = useCallback((i, e) => {
        e?.preventDefault();
        e?.stopPropagation();
        setIndex(i);
    }, []);

    // 0 photos → placeholder
    if (safePhotos.length === 0) {
        return (
            <div className={`relative w-full ${className} bg-muted/40 flex items-center justify-center text-muted-foreground overflow-hidden`} data-testid="photo-carousel-empty">
                {placeholder ? (
                    <img src={placeholder} alt={alt} className="w-full h-full object-cover" />
                ) : (
                    <ImageIcon className="w-10 h-10 opacity-30" />
                )}
                {badge && <div className="absolute top-3 right-3">{badge}</div>}
            </div>
        );
    }

    // 1 photo → static image
    if (safePhotos.length === 1) {
        return (
            <div className={`relative w-full ${className} overflow-hidden`} data-testid="photo-carousel-single">
                <img src={resolvePhoto(safePhotos[0])} alt={alt} className="w-full h-full object-cover" />
                {badge && <div className="absolute top-3 right-3">{badge}</div>}
            </div>
        );
    }

    // 2+ photos → carousel
    return (
        <div className={`relative w-full ${className} overflow-hidden group bg-muted/30`} data-testid="photo-carousel">
            <div
                className="flex h-full transition-transform duration-300 ease-out"
                style={{ transform: `translateX(-${index * 100}%)`, width: `${safePhotos.length * 100}%` }}
            >
                {safePhotos.map((p, i) => (
                    <div key={i} className="relative flex-shrink-0 h-full" style={{ width: `${100 / safePhotos.length}%` }}>
                        <img
                            src={resolvePhoto(p)}
                            alt={`${alt} ${i + 1}`}
                            className="w-full h-full object-cover"
                            loading={i === 0 ? 'eager' : 'lazy'}
                        />
                    </div>
                ))}
            </div>

            {/* Arrows */}
            <button
                type="button"
                onClick={goPrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white z-10"
                aria-label="Photo précédente"
                data-testid="carousel-prev"
            >
                <ChevronLeft className="w-4 h-4 text-scalimmo-navy" />
            </button>
            <button
                type="button"
                onClick={goNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white z-10"
                aria-label="Photo suivante"
                data-testid="carousel-next"
            >
                <ChevronRight className="w-4 h-4 text-scalimmo-navy" />
            </button>

            {/* Dots */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 px-2 py-1 rounded-full bg-black/30 backdrop-blur-sm z-10">
                {safePhotos.map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={(e) => goTo(i, e)}
                        className={`rounded-full transition-all ${i === index ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/60'}`}
                        aria-label={`Photo ${i + 1}`}
                        data-testid={`carousel-dot-${i}`}
                    />
                ))}
            </div>

            {/* Photo counter */}
            {showCounter && (
                <div className="absolute top-3 left-3 bg-black/55 text-white text-xs px-2 py-0.5 rounded-full backdrop-blur-sm z-10">
                    {index + 1} / {safePhotos.length}
                </div>
            )}

            {badge && <div className="absolute top-3 right-3 z-10">{badge}</div>}
        </div>
    );
};
